import _ from "lodash";
import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";

// HOCs and utilities
import I18nHOC from "UIComponents/I18nHOC";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import {
  createComment,
  deleteComment,
  updateComment,
  upsertActivityPostCommentsAsRead,
} from "Post/modules/PostModule";
import { getCommentsOfPostQuery } from "AppComponents/Post/modules/PostQuery";
import { getCommentsOfPostFromCache } from "AppComponents/Post/modules/PostGraphqlHelpers";
import classNames from "classnames";
import {
  getDefaultStudentNameOrderPreference,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
// UI
import classes from "../CardComments.scss";
import ChatbarWrapper from "UIComponents/ChatbarWrapper/ChatbarWrapper";
import {
  updateBadgeCountOnDeleteInProjectDetailsCache,
  getCurrentProjectGroupDetails,
} from "Projects/modules/ProjectModules";
import { scrollIntoView, generateRandomId } from "Utils";
import CommentFeed from "./CommentFeed";
import { PostCommentFragment } from "Post/modules/PostFragments";
import { canManagePortfolioActions } from "Post/utils";
import { ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION } from "Constants/permissionConstants";
import { MOBILE_MEDIA_QUERY } from "Constants/stringConstants";
import ACLStore from "lib/aclStore";
import { Dialog, DSProvider, useDSContext } from "@toddle-edu/ds-web";
import { isRoutinesRoute } from "AppComponents/Routines/utils";
import useMediaQuery from "Hooks/UseMediaQuery";

const commentStyles = (
  isSidePaneContent,
  showCommentBox,
  comments,
  canPublishComment,
  isProjectPostCard
) => ({
  commentIcon: {
    display: "flex",
    marginBottom: "8px",
    paddingTop: "8px",
  },
  commentListStyles: {
    paddingBottom:
      !isSidePaneContent &&
      (showCommentBox || comments?.length) &&
      canPublishComment &&
      !isProjectPostCard
        ? "16px"
        : "0px",
  },
});

const styles = {
  chatBarContainerStyle: { paddingBottom: "16px" },
  sidePaneCommentStyleV2: {
    width: "508px",
  },
  sidePaneCommentStyleV2FullWidth: {
    width: "568px",
  },
};

const BLANK_ARRAY = [];

const CardComments = props => {
  const {
    parentId,
    parentType,
    comments,
    totalCount,
    unreadCount,
    userId,
    userType,
    showCommentBox,
    createComment,
    deleteComment,
    updateComment,
    t,
    fetchMoreFeed,
    canComment = true,
    canDelete = true,
    canPublishComment,
    hideCommentBox = false,
    hideCommentSection = false,
    isSidePaneContent,
    containerRef,
    editComment = {},
    editSidePaneComment = () => {},
    showCommentBar = false,
    setCommentBar = () => {},
    showCommentBadge,
    projectId,
    showViewCommentButtonAtTop,
    showViewCommentButtonText,
    showViewAllCommentsButton,
    setShowViewAllCommentsButton,
    commentsFromProps,
    cacheVariables,
    postFragment = PostCommentFragment.post,
    upsertActivityPostCommentsAsRead,
    projectGroupType,
    defaultStudentNameOrderPreference,
    academicYearId,
    hasManageAccessForStudentPortfolio,
    hasAttachments,
    isProjectPostCard,
    addPaddingTopFromChatbarWrapper = true,
    hasRoutinesManageAccess,
    commentContainerClass,
  } = props;

  const { dsVersion } = useDSContext();
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  const isDSV2 = dsVersion === "2.0";
  const previousTotalCount = useRef(totalCount);
  const currentCommentCount = useRef(0);
  const viewCommentCount = useRef(0);
  const [commentLabel, setCommentLabel] = useState(
    _.get(editComment, "label", "")
  );
  const [voiceAttachment, setVoiceAttachment] = useState({});
  const [commentToEdit, setCommentToEdit] = useState({});
  const [deleteModalDetails, setDeleteModalDetails] = useState({
    showDeleteModal: false,
    commentToDelete: null,
  });
  const commentReadCount = useRef(0);
  const isJournal = _.includes(window.location.href, "/journal");
  const getLatestCommentsV2 = () => {
    let latestComments = [];
    if (_.size(commentsFromProps) > 0 && _.size(comments) == 0) {
      latestComments = [...commentsFromProps];
    }
    if (_.size(comments) > 0) {
      latestComments.push(...(comments || []));
    }
    return _.uniqBy(latestComments, "node.id");
  };

  const latestComment = useMemo(() => getLatestCommentsV2(), [
    commentsFromProps,
    comments,
  ]);
  const isStaff = userType == "staff";

  const [mode, setMode] = useState("VIEW");

  useEffect(() => {
    previousTotalCount.current = totalCount;
  }, [totalCount]);

  useEffect(() => {
    showCommentBar &&
      isSidePaneContent &&
      scrollIntoView({
        element: containerRef.current,
        block: "end",
        inline: "end",
      });
  }, [showCommentBar]);

  const onEditComment = useCallback(commentToEdit => {
    setMode("EDIT");
    setCommentLabel(_.get(commentToEdit, "label"));
    setCommentToEdit(commentToEdit);
    _.get(commentToEdit, "item.attachments")?.length &&
      setVoiceAttachment(_.get(commentToEdit, "item.attachments[0]", {}));

    isSidePaneContent &&
      editSidePaneComment &&
      editSidePaneComment(commentToEdit);
  }, []);

  useEffect(() => {
    if (!_.isEmpty(editComment)) {
      setMode("EDIT");
      setCommentLabel(_.get(editComment, "label"));
      setCommentToEdit(editComment);
      _.get(editComment, "item.attachments")?.length &&
        setVoiceAttachment(_.get(editComment, "item.attachments[0]", {}));
    }
  }, [editComment]);

  const onDeleteComment = useCallback(commentToDelete => {
    setDeleteModalDetails({
      showDeleteModal: true,
      commentToDelete: commentToDelete,
    });
  }, []);

  const onConfirmDeleteComment = () => {
    if (!_.isNil(_.get(deleteModalDetails.commentToDelete, "id", undefined))) {
      deleteComment({
        parentId,
        parentType,
        commentId: _.get(deleteModalDetails.commentToDelete, "id", undefined),
        viewAllComment: showViewAllCommentsButton,
        cacheVariables: showViewAllCommentsButton ? cacheVariables : undefined,
        viewCommentCount: viewCommentCount.current,
        fragment: postFragment,
      });
      previousTotalCount.current -= 1;
      if (viewCommentCount.current > 0) viewCommentCount.current -= 1;
    }
  };

  const handleDeleteComment = () => {
    onConfirmDeleteComment();
    updateBadgeCountOnDeleteInProjectDetailsCache({
      projectId,
      type: "MESSAGE",
      projectGroupType,
      academicYearId,
    });
    setDeleteModalDetails({
      showDeleteModal: false,
      commentToDelete: null,
    });
  };

  const handleCancelDeleteComment = () => {
    setDeleteModalDetails({
      showDeleteModal: false,
      commentToDelete: null,
    });
  };

  const changeToViewMode = () => {
    setMode("VIEW");
    setCommentLabel("");
    setCommentToEdit({});
    setVoiceAttachment({});
  };

  const onCreateComment = messageData => {
    if (!_.isEmpty(commentToEdit) && mode === "EDIT") {
      updateComment({
        id: _.get(commentToEdit, "id"),
        parentId,
        parentType,
        label: _.trim(messageData.label),
        attachments: messageData?.attachments?.length
          ? messageData.attachments
          : [],
        fragment: postFragment,
      });
      changeToViewMode();
      return;
    }

    createComment({
      parentId,
      parentType,
      subEntityType: "COMMENT",
      label: _.trim(commentLabel ? commentLabel : messageData.label),
      attachments: messageData?.attachments?.length
        ? messageData.attachments
        : [],
      cacheVariables: showViewAllCommentsButton ? cacheVariables : undefined,
      fragment: postFragment,
    });
    // previousTotalCount.current += 1;
    if (!totalCount) currentCommentCount.current += 1;
    //this is to handle the comment count if no comment is present in the post for handling view comments button
    viewCommentCount.current += 1;
    //this is to handle if already there some comments present in the post

    isSidePaneContent &&
      scrollIntoView({
        element: containerRef.current,
        block: "end",
        inline: "end",
      });

    changeToViewMode();
  };

  const memoizedMessageData = useMemo(() => {
    return {
      label: commentLabel,
      attachments: _.isEmpty(voiceAttachment) ? [] : [{ ...voiceAttachment }],
      id: generateRandomId(),
    };
  }, [commentLabel, voiceAttachment]);

  // update comment read count only for student activity post comments
  const updateCommentReadCount = () => {
    if (parentType !== "STUDENT_ACTIVITY" || unreadCount <= 0) return;

    let currentCommentReadCount = commentReadCount.current;

    if (currentCommentReadCount > totalCount) return;

    ++currentCommentReadCount;

    if (currentCommentReadCount === totalCount) {
      onAllCommentsRead();
    }

    commentReadCount.current = currentCommentReadCount;
  };

  const onAllCommentsRead = () => {
    upsertActivityPostCommentsAsRead({ userId, entityId: parentId });
  };
  const isRnpEnabled = ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION);
  const hasCommentAccess =
    isRnpEnabled && isJournal
      ? !_.includes(window.location.href, "/studentFeed")
        ? canManagePortfolioActions(userType)
        : hasManageAccessForStudentPortfolio
      : isRoutinesRoute()
      ? isRnpEnabled && userType === "staff"
        ? hasRoutinesManageAccess
        : true
      : true;

  let shouldShowCommentBox =
    !hideCommentBox &&
    (!isRnpEnabled ? canComment : true) &&
    canPublishComment &&
    hasCommentAccess;
  if (!isSidePaneContent) {
    shouldShowCommentBox = !showViewAllCommentsButton && hasCommentAccess;
  }

  if (
    (!_.isEmpty(commentToEdit) || showCommentBar || showCommentBox) &&
    hasCommentAccess
  ) {
    shouldShowCommentBox = true;
  }
  const shouldShowUpdateCommentReadCount =
    parentType === "STUDENT_ACTIVITY" && unreadCount > 0;

  return (
    <div
      className={classes.commentsListContainer}
      style={
        commentStyles(
          isSidePaneContent,
          showCommentBox,
          comments,
          canPublishComment,
          dsVersion,
          isProjectPostCard
        ).commentListStyles
      }
    >
      {!hideCommentSection && (
        <CommentFeed
          isSidePaneContent={isSidePaneContent}
          comments={latestComment}
          showCommentBox={showCommentBox}
          t={t}
          showViewAllCommentsButton={showViewAllCommentsButton}
          setShowViewAllCommentsButton={setShowViewAllCommentsButton}
          totalCount={totalCount}
          userId={userId}
          canComment={canComment}
          isStaff={isStaff}
          currentCommentCount={currentCommentCount}
          viewCommentCount={viewCommentCount}
          fetchMoreFeed={fetchMoreFeed}
          onEditComment={onEditComment}
          onDeleteComment={onDeleteComment}
          showCommentBadge={showCommentBadge}
          showViewCommentButtonAtTop={showViewCommentButtonAtTop}
          showViewCommentButtonText={showViewCommentButtonText}
          canDelete={!isRnpEnabled ? canDelete : hasCommentAccess}
          updateCommentReadCount={
            shouldShowUpdateCommentReadCount ? updateCommentReadCount : null
          }
          defaultStudentNameOrderPreference={defaultStudentNameOrderPreference}
          isProjectPostCard={isProjectPostCard}
        />
      )}
      {shouldShowCommentBox ? (
        <div
          className={classNames(
            {
              [classes.marginTop16]: !isSidePaneContent,
              [classes.commentBarContainer]: isSidePaneContent,
            },
            commentContainerClass
          )}
          style={
            isRoutinesRoute() && !isMobile
              ? hasAttachments
                ? styles.sidePaneCommentStyleV2
                : styles.sidePaneCommentStyleV2FullWidth
              : {}
          }
        >
          <ChatbarWrapper
            messageData={memoizedMessageData}
            chatBarContainerStyle={
              isDSV2 && addPaddingTopFromChatbarWrapper
                ? styles.chatBarContainerStyle
                : {}
            }
            sendMessage={onCreateComment}
            isTaggingEnabled={false}
            collaborators={BLANK_ARRAY}
            parentType={parentType}
            mode={mode}
            inputTextPlaceHolder={
              isProjectPostCard
                ? t("common:type_your_comment_here")
                : t("journal:type_your_message_here")
            }
            showAddButton={false}
            cancelEditMessage={changeToViewMode}
            focus={isSidePaneContent ? showCommentBar : showCommentBox}
            testIdPrefix="journal-feed-comment"
            alwaysShowHelperText={isProjectPostCard}
          />
        </div>
      ) : null}
      <DSProvider dsVersion="2.0">
        {deleteModalDetails.showDeleteModal ? (
          <Dialog
            showModal={true}
            showCloseButton={true}
            title={t("common:delete_label", {
              label: t("common:comment"),
            })}
            onButtonTwoClick={handleDeleteComment}
            onButtonOneClick={handleCancelDeleteComment}
            message={t("journal:delete_comment_confirmation_message")}
            onClose={handleCancelDeleteComment}
            buttonOneText={t("common:cancel")}
            buttonTwoText={t("common:delete")}
          />
        ) : null}
      </DSProvider>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const userId = _.get(state, "login.userInfo.id");
  const userType = _.get(state, "login.userInfo.user_type");

  const projectId = _.get(ownProps, "params.projectId", null);
  const { type: projectGroupType } = getCurrentProjectGroupDetails({
    state,
  });

  const defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference(
    state
  );

  return {
    isCommunity: state.login.activeTab == "community",
    commentsKey: "comments",
    userId,
    userType,
    first: 1,
    projectId,
    projectGroupType,
    defaultStudentNameOrderPreference,
    academicYearId: getSelectedAcademicYearId(state),
  };
};

const mapActionCreators = {
  createComment,
  deleteComment,
  updateComment,
  upsertActivityPostCommentsAsRead,
};

const CardCommentsWithHOC = compose(
  I18nHOC({ resource: ["common", "journal"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getCommentsOfPostQuery, {
    name: "getCommentsOfPost",
    skip: ({ totalCount, commentsFromProps, showViewAllCommentsButton }) =>
      totalCount == 0 ||
      (_.size(commentsFromProps, 0) > 0 && showViewAllCommentsButton) ||
      _.size(commentsFromProps) == totalCount,
    options: ({ parentId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: parentId,
      },
    }),
    props({ getCommentsOfPost, ownProps: { parentId, commentsKey } }) {
      const nodeFields = getCommentsOfPostFromCache({
        id: parentId,
      });
      const comments = _.get(nodeFields, commentsKey, []);
      return {
        getCommentsOfPost,
        hasUnreadCommentFetched: false,
        totalCount: _.get(comments, "totalCount", 0),
        comments: _.uniqBy([..._.get(comments, "edges", [])], "node.id"),
        hasNextPage: _.get(comments, "pageInfo.hasNextPage", false),
        isData: !_.isEmpty(nodeFields),
        isLoading:
          getCommentsOfPost["networkStatus"] == 1 ||
          getCommentsOfPost["networkStatus"] == 2,
      };
    },
  })
)(CardComments);

const CardCommentsWrapper = props => {
  const { showViewAllComments, commentsFromProps = [], totalCount } = props;
  const [showViewAllCommentsButton, setShowViewAllCommentsButton] = useState(
    showViewAllComments || _.size(commentsFromProps) != totalCount
  );
  return (
    <CardCommentsWithHOC
      {...props}
      showViewAllCommentsButton={showViewAllCommentsButton}
      setShowViewAllCommentsButton={setShowViewAllCommentsButton}
    />
  );
};

export default CardCommentsWrapper;
CardComments.displayName = "CardCommentsV2";
